import React, { useEffect } from 'react';

export default function Tips() {
  useEffect(() => {
    // Set the document title
    document.title = "Staging Tips for Furniture Flippers | FurniSnapp";
  }, []);

  return (
    <div className="bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Staging Tips for Furniture Flippers
          </h1>
          
          <p className="text-gray-600 mb-6">
            Transform your furniture photos with perfect staging to maximize appeal and selling potential
          </p>
        </div>
        
        {/* How It Works Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-600 mb-4">
            How Our Prompt System Works
          </h2>
          
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Our system combines <span className="font-semibold">Design Style</span> + <span className="font-semibold">Room Environment</span> to create the perfect staging prompt. You can also edit the final prompt to customize it further.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg flex-1">
              <h3 className="font-medium text-blue-600 mb-2">Step 1: Choose Furniture Type</h3>
              <p className="text-gray-700 text-sm mb-2">This determines the appropriate room environment for your furniture.</p>
              <div className="border border-gray-200 rounded p-2 bg-white">
                <p className="text-xs text-gray-500">Example: For a dresser, we'll use</p>
                <p className="text-sm font-medium">"bedroom with white walls, area rug, bedside lamps"</p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg flex-1">
              <h3 className="font-medium text-blue-600 mb-2">Step 2: Choose Design Style</h3>
              <p className="text-gray-700 text-sm mb-2">This adds stylistic elements to the environment.</p>
              <div className="border border-gray-200 rounded p-2 bg-white">
                <p className="text-xs text-gray-500">Example: For Mid-Century Modern, we'll add</p>
                <p className="text-sm font-medium">"mid-century modern room, walnut tones, geometric patterns, brass accents"</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-600 mb-2">Final Combined Prompt</h3>
            <p className="text-gray-700 text-sm mb-2">The system combines these elements into one prompt:</p>
            <div className="border border-gray-200 rounded p-3 bg-white">
              <p className="text-sm font-medium">"mid-century modern room, walnut tones, geometric patterns, brass accents for a bedroom with white walls, area rug, bedside lamps"</p>
            </div>
            <p className="text-gray-500 text-xs mt-2">You can edit this final prompt before generating your image.</p>
          </div>
        </div>
        
        {/* Prompt Writing Tips - First Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-600 mb-4">
            Effective Staging Prompts
          </h2>
          
          <ul className="space-y-4 mb-6">
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Focus exclusively on surroundings, not the furniture</h3>
                <p className="text-gray-600 text-sm">Describe only the environment around your piece - the AI will preserve your furniture.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Use descriptive nouns and adjectives</h3>
                <p className="text-gray-600 text-sm">Focus on colors, materials, and style words rather than actions.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Edit the auto-generated prompt if needed</h3>
                <p className="text-gray-600 text-sm">Our system creates a starting point, but you can customize it for your specific piece.</p>
              </div>
            </li>
          </ul>
          
          {/* Design Style Examples - First */}
          <h3 className="font-bold text-lg text-blue-600 mb-3">Design Style Examples</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Modern Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Minimalist space, white walls, concrete floors, large windows"</li>
              <li>"Contemporary space, neutral palette, clean lines, statement lighting"</li>
              <li>"Modern apartment, floor-to-ceiling windows, sleek furniture, monochromatic"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Mid-Century Modern Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Mid-century room, walnut paneling, geometric rug, brass accents"</li>
              <li>"1960s inspired space, teak elements, orange accents, sunburst clock"</li>
              <li>"Retro living area, sputnik chandelier, wood tones, graphic art"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Farmhouse Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Rustic farmhouse, shiplap walls, wooden beams, vintage accessories"</li>
              <li>"Country kitchen, butcher block counters, apron sink, mason jars"</li>
              <li>"Cozy farmhouse living room, neutral colors, woven baskets, linen textiles"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Industrial Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Industrial loft, exposed brick, metal pipes, Edison bulbs"</li>
              <li>"Warehouse conversion, concrete floors, steel beams, factory windows"</li>
              <li>"Urban industrial space, distressed wood, black metal, raw textures"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Bohemian Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Boho living space, layered rugs, macramé wall hanging, abundant plants"</li>
              <li>"Eclectic bohemian room, colorful textiles, floor cushions, hanging plants"</li>
              <li>"Free-spirited space, mixed patterns, global artifacts, natural materials"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">Traditional Style</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Classic living room, crown molding, Persian rug, symmetrical layout"</li>
              <li>"Elegant traditional space, rich wood tones, tufted upholstery, antique lamps"</li>
              <li>"Formal dining room, wainscoting, crystal chandelier, damask wallpaper"</li>
            </ul>
          </div>
          
          {/* Room Environment Examples - Second */}
          <h3 className="font-bold text-lg text-blue-600 mb-3">Room Environment Examples by Furniture Type</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">For Dressers/Cabinets</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Bedroom with white walls, area rug, bedside lamps"</li>
              <li>"Bedroom with nightstands, soft lighting, window treatments"</li>
              <li>"Bedroom with hardwood floors, large windows, plants"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">For Sofas/Couches</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Living room with hardwood floors, coffee table, wall art"</li>
              <li>"Living room with area rug, large windows, statement lighting"</li>
              <li>"Cozy corner with bookshelves, warm lighting, textured throw"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">For Dining Tables</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Dining area with pendant lighting, area rug, window view"</li>
              <li>"Open concept dining area, hardwood floors, styled tablescape"</li>
              <li>"Dining space with wainscoting, chandelier, French doors"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h4 className="font-medium mb-2">For Accent Chairs</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Cozy corner with side table, reading lamp, bookshelf"</li>
              <li>"Living room corner with floor lamp, window, textured rug"</li>
              <li>"Bright space with large windows, plants, minimal decor"</li>
            </ul>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">For Coffee Tables</h4>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
              <li>"Living room with sectional sofa, area rug, large windows"</li>
              <li>"Lounge area with comfortable seating, textured rug, decorative objects"</li>
              <li>"Bright living space with natural light, plants, minimal decor"</li>
            </ul>
          </div>
        </div>
        
        {/* Photography Tips */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-600 mb-4">
            Photography Tips for Furniture Flippers
          </h2>
          
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Capture the entire piece</h3>
                <p className="text-gray-600 text-sm">Make sure your furniture is fully visible in the frame with some space around it for the AI to add staging elements.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Use neutral backgrounds when possible</h3>
                <p className="text-gray-600 text-sm">Photograph against a simple wall or backdrop to make it easier for the AI to replace with a staged setting.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Shoot from a natural viewing angle</h3>
                <p className="text-gray-600 text-sm">Photograph from the angle a potential buyer would typically view the piece (eye level for tall pieces, slightly above for tables).</p>
              </div>
            </li>
          </ul>
        </div>
        
        {/* Troubleshooting Common Issues */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-blue-600 mb-4">
            Troubleshooting Common Issues
          </h2>
          
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Furniture looks distorted in the staged image</h3>
                <p className="text-gray-600 text-sm">Take your photo straight-on, not at extreme angles. Make sure the piece is well-lit and clearly visible.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Staging doesn't match the furniture style</h3>
                <p className="text-gray-600 text-sm">Be very specific about the design style in your prompt. Mention the era or specific style that matches your furniture piece.</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 text-blue-500 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">Background looks unrealistic</h3>
                <p className="text-gray-600 text-sm">Use more specific architectural details in your prompt, like "large windows," "white shiplap walls," or "hardwood flooring."</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
} 